import Refresh from "@mui/icons-material/Refresh"
import {
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material"
import { FC, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { SharedAccessPatternSaEnum } from "shared/sharedAccessPatternsData"
import { Tenant } from "shared/tenant"
import { useAppDispatch, useAppSelector } from "src/app/hooks"
import { selectTenantId, setTenantId } from "src/features/me/meSlice"
import { useGetThingsQuery } from "src/features/things/thingsApi"
import { selectSaList, thingsActions } from "src/features/things/thingsSlice"
import OsIamMembers from "./OsIamMembers"
import SaLayout from "./SaLayout"

const eyebrowSx = {
  fontSize: "0.72rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase" as const,
  color: "primary.main",
}

const titleSx = {
  mt: 0.5,
  fontWeight: 700,
  letterSpacing: "-0.02em",
  color: "text.primary",
  lineHeight: 1.15,
}

const tonalIconBtnSx = {
  bgcolor: "rgba(35, 66, 133, 0.06)",
  color: "primary.main",
  "&:hover": { bgcolor: "rgba(35, 66, 133, 0.12)" },
}

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
}

export const SaOrgMembersPage: FC = () => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()

  const tenantId = useAppSelector(selectTenantId)

  useEffect(() => {
    dispatch(thingsActions.clearSaList())
  }, [dispatch])

  const tenants = useAppSelector((state) => selectSaList<Tenant>(state))

  const { isLoading, refetch } = useGetThingsQuery({
    params: {
      "ap.name": SharedAccessPatternSaEnum.SaGetTenants,
      "ap.tenantOnly": true,
    },
  })

  return (
    <SaLayout selectedMenuItemLabelKey="orgMembers">
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          width: "100%",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          flexWrap="wrap"
          gap={2}
          sx={{ mb: 4 }}
        >
          <Box>
            <Typography sx={eyebrowSx}>{t("adminApp")}</Typography>
            <Typography variant="h4" sx={titleSx}>
              {t("orgMembers")}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {isLoading ? (
              <CircularProgress size={22} />
            ) : (
              <IconButton onClick={refetch} sx={tonalIconBtnSx} size="small">
                <Refresh fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Stack>

        {/* Org selector card */}
        <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 3 }}>
          <Typography
            sx={{
              fontSize: "0.72rem",
              fontWeight: 600,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "text.secondary",
              mb: 1.25,
            }}
          >
            {t("selectOrg")}
          </Typography>
          <Select
            value={tenantId}
            onChange={(event) => {
              dispatch(setTenantId(event.target.value as string))
            }}
            defaultValue="-1"
            size="small"
            sx={{ minWidth: 320 }}
          >
            {tenants.map((tenant) => (
              <MenuItem key={tenant.id} value={tenant.id} sx={{ mb: 2 }}>
                <Stack direction="column" spacing={1}>
                  <Typography variant="body1" fontWeight="bold">
                    {tenant.name}
                  </Typography>
                  <Typography variant="body2">{tenant.id}</Typography>
                  <Typography variant="body2">{`${t("createdAt")} ${new Date(
                    tenant.createdAt,
                  ).toLocaleString()}`}</Typography>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Members list card */}
        <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
          <OsIamMembers />
        </Box>
      </Box>
    </SaLayout>
  )
}

export default SaOrgMembersPage
